import { Link, useParams } from "react-router-dom";
import type { Bundle } from "@otto/lehrgang";
import { useSession } from "@otto/plattform";
import { useClass } from "../../classes/lib/data.js";
import { useClassCourses, type ClassCourse } from "../../courses/lib/data.js";
import { CertificateButton } from "../../certificates/components/CertificateButton.js";
import { ClassOverview } from "./ClassOverview.js";
import { ClassSpeedSummary } from "./ClassSpeedSummary.js";
import { CourseMatrix } from "./CourseMatrix.js";
import { orphanKeys, useResults } from "../lib/data.js";
import { overviewRows } from "../lib/overview.js";
import styles from "../../../shared/components/shell.module.css";

/**
 * Ergebnisse einer Klasse – eine Zeile je Kind.
 *
 * Vorher war die Seite nach Lehrgängen gegliedert: je Lehrgang eine Tabelle mit
 * allen Kindern, dazu eine zweite fürs Tempo. Drei Lehrgänge ergaben sechs
 * Tabellen, und der Stand eines Kindes stand an sechs Stellen. Jetzt ist das
 * Kind die Zeile und der Lehrgang die Spaltengruppe.
 *
 * Weil die Lehrgänge sich an der Klasse ansammeln statt sich abzulösen, bleibt
 * der abgeschlossene Grundkurs 1 hier stehen, wenn Grundkurs 2 dazukommt. Genau
 * dafür hätte es sonst eine Verlaufstabelle gebraucht.
 *
 * Verwaltet werden die Lehrgänge nicht hier, sondern auf der Klassenseite: das
 * hier ist die Ansicht, dort ist die Verwaltung.
 */
export function Results() {
  const { classId = "" } = useParams();
  const { client } = useSession();
  const schoolClass = useClass(client, classId);
  const courses = useClassCourses(client, classId);
  const results = useResults(client, classId);

  const readable = (courses.data ?? []).filter(
    (course): course is ClassCourse & { bundle: Bundle } => course.bundle !== null,
  );
  const orphans =
    results.data && courses.data
      ? orphanKeys(
          results.data,
          readable.map((c) => c.bundle),
        )
      : [];
  const unreadable = (courses.data ?? []).filter((c) => c.bundle === null);
  const rows =
    results.data && courses.data ? overviewRows(results.data, courses.data) : [];

  return (
    <div className={styles.card}>
      {/* Zurück zu den Klassen, nicht zu den Codes: die Ergebnisse hängen an der
          Klasse, nicht an ihrer Codeliste. Beide sind von der Übersicht aus
          gleich weit entfernt. */}
      <p className={styles.subtitle} style={{ marginBottom: 10 }}>
        <Link to="/classes">← Klassen</Link>
      </p>
      <h1 className={styles.title}>
        Ergebnisse {schoolClass.data ? `· ${schoolClass.data.name}` : ""}
      </h1>
      <p className={styles.subtitle}>
        Eine Zeile je Kind. Das Band zeigt die Lektionen des Lehrgangs, ein Feld je
        Lektion, eingefärbt nach ihren Sternen. Für den ganzen Stand eines Kindes auf den
        Übungscode klicken.
      </p>

      {unreadable.map((course) => (
        <div
          key={course.curriculumId}
          className={`${styles.notice} ${styles.noticeError}`}
        >
          {course.title}: Fassung {course.version} ist nicht lesbar.
        </div>
      ))}

      {(courses.isPending || results.isPending) && (
        <p className={styles.empty}>Ergebnisse werden geladen …</p>
      )}

      {results.data?.length === 0 && (
        <p className={styles.empty}>Noch keine Übungscodes in dieser Klasse.</p>
      )}

      {results.data && results.data.length > 0 && (
        <>
          {/* Die Urkunde steht hier und nicht mehr bei den Codes: was auf ihr
              steht, ist das Ergebnis des Kindes. Sie neben der Codeliste zu
              drucken hieß, sie an der Stelle anzubieten, an der man den Stand
              gerade NICHT sieht. */}
          <CertificateButton
            classId={classId}
            className={schoolClass.data?.name ?? ""}
            schoolYear={schoolClass.data?.school_year ?? ""}
          />
          <ClassSpeedSummary courses={readable} rows={results.data} />
          <ClassOverview classId={classId} rows={rows} />
          <CourseMatrix courses={readable} rows={results.data} />
        </>
      )}

      {orphans.length > 0 && (
        // Nicht verschwiegen: die Übung hat stattgefunden, sie passt nur nicht
        // mehr in die Tabelle. Sonst sucht eine Lehrkraft verlorenen Fortschritt.
        <div
          className={`${styles.notice} ${styles.spaced}`}
          style={{ background: "var(--accent-pale)" }}
        >
          Zu {orphans.length} Übungen aus einer früheren Fassung oder einem entfernten
          Lehrgang gibt es Ergebnisse, die hier nicht abgebildet sind. Sie gehen nicht
          verloren.
        </div>
      )}
    </div>
  );
}
